import React, { useState, useMemo, useCallback } from "react";
import { useTranslate } from "@refinedev/core";
import { DateField, List } from "@refinedev/mui";
import { DataGrid, GridColDef, GridPaginationModel } from "@mui/x-data-grid";
import {
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import { Visibility } from "@mui/icons-material";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import { useNavigate } from "react-router-dom";
import { useUnifiedHistory } from "../../hooks/useUnifiedHistory";
import type { UnifiedHistoryItem } from "../../hooks/useUnifiedHistory";
import type { EventType, UnifiedHistoryFilters } from "../../types/events";
import { EventTypeChip } from "../../components/EventTypeChip";
import { ALERT_STATUS_COLORS } from "../../constants/eventColors";
import { AddressCell } from "../../components/table/AdressCell";
import { getDuration } from "../../utils/getDuration";
import { HistoryFilters } from "./components/HistoryFilters";

export const HistoryList: React.FC = () => {
  const translate = useTranslate();
  const navigate = useNavigate();

  const [filters, setFilters] = useState<UnifiedHistoryFilters>({
    hideActive: true,
  });
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });

  const { data, total, isLoading } = useUnifiedHistory(
    filters,
    paginationModel.page + 1, // DataGrid is 0-indexed, API is 1-indexed
    paginationModel.pageSize,
  );

  const handleFiltersChange = useCallback((newFilters: UnifiedHistoryFilters) => {
    setFilters(newFilters);
    setPaginationModel((prev) => ({ ...prev, page: 0 })); // Reset to first page
  }, []);

  const userLocale = navigator.language;

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: "eventType",
        headerName: translate("history.fields.type", "Type"),
        width: 130,
        sortable: false,
        renderCell: ({ value }) => (
          <EventTypeChip eventType={value as EventType} />
        ),
      },
      {
        field: "userName",
        headerName: translate("history.fields.user", "User"),
        flex: 1,
        minWidth: 150,
        sortable: false,
        renderCell: ({ row }) => (
          <span>{row.userName || row.userEmail}</span>
        ),
      },
      {
        field: "subGroup",
        headerName: translate("history.fields.subGroup", "Sub-group"),
        width: 160,
        sortable: false,
        renderCell: ({ row }) => {
          const user = row.data?.user;
          if (!user?.subGroup?.name) return null;
          return (
            <Chip
              icon={<AccountTreeIcon sx={{ fontSize: "14px !important" }} />}
              label={user.subGroup.name}
              size="small"
              variant="outlined"
              sx={{
                height: "22px",
                "& .MuiChip-label": { px: 0.75, fontSize: "0.75rem" },
                "& .MuiChip-icon": { color: "primary.main", ml: 0.5 },
              }}
            />
          );
        },
      },
      {
        field: "timestamp",
        headerName: translate("history.fields.date", "Date"),
        width: 180,
        sortable: false,
        renderCell: ({ value }) => (
          <DateField locales={userLocale} value={value} format="DD/MM/YYYY HH:mm" />
        ),
      },
      {
        field: "status",
        headerName: translate("history.fields.status", "Status"),
        width: 140,
        sortable: false,
        renderCell: ({ row }) => {
          if (row.eventType === "alert") {
            const status = row.data?.status as string;
            const color = ALERT_STATUS_COLORS[status as keyof typeof ALERT_STATUS_COLORS];
            return (
              <Chip
                label={translate(`monitoring.status.${status?.toLowerCase()}`, status)}
                size="small"
                sx={{
                  backgroundColor: color ? `${color}20` : undefined,
                  color: color || undefined,
                  fontWeight: 600,
                }}
              />
            );
          }
          if (row.eventType === "journey") {
            const hasEnded = row.data?.hasEnded;
            return (
              <Chip
                label={hasEnded ? translate("history.status.ended", "Ended") : translate("history.status.active", "Active")}
                size="small"
                color={hasEnded ? "default" : "success"}
              />
            );
          }
          if (row.eventType === "forbidden_zone") {
            const exitedAt = row.data?.exitedAt;
            return (
              <Chip
                label={exitedAt ? translate("history.status.exited", "Exited") : translate("history.status.stillInside", "Still inside")}
                size="small"
                color={exitedAt ? "default" : "warning"}
              />
            );
          }
          return null;
        },
      },
      {
        field: "duration",
        headerName: translate("history.fields.duration", "Duration"),
        width: 100,
        sortable: false,
        renderCell: ({ row }) => {
          if (row.eventType === "alert") {
            return getDuration({
              createdAt: row.timestamp,
              endedAt: row.data?.updatedAt as string,
            });
          }
          if (row.eventType === "journey") {
            return getDuration({
              createdAt: row.timestamp,
              endedAt: row.data?.updatedAt as string,
              isActive: !row.data?.hasEnded,
            });
          }
          if (row.eventType === "forbidden_zone") {
            return getDuration({
              createdAt: row.data?.enteredAt as string,
              endedAt: row.data?.exitedAt as string,
              isActive: !row.data?.exitedAt,
            });
          }
          return "-";
        },
      },
      {
        field: "location",
        headerName: translate("history.fields.location", "Location"),
        flex: 1,
        minWidth: 200,
        sortable: false,
        renderCell: ({ row }) => {
          if (row.eventType === "alert" && row.latitude && row.longitude) {
            return <AddressCell latitude={row.latitude} longitude={row.longitude} />;
          }
          if (row.eventType === "forbidden_zone") {
            const place = row.data?.place as { emoji?: string; name?: string } | undefined;
            return place ? `${place.emoji || ""} ${place.name || ""}`.trim() : "-";
          }
          return "-";
        },
      },
      {
        field: "actions",
        headerName: translate("table.actions", "Actions"),
        width: 80,
        sortable: false,
        align: "center",
        headerAlign: "center",
        renderCell: ({ row }) => (
          <Tooltip title={translate("buttons.show", "View")}>
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleRowClick(row)}
            >
              <Visibility />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    [translate, userLocale],
  );

  function handleRowClick(row: UnifiedHistoryItem) {
    if (row.eventType === "alert") {
      if (row.isActive) {
        navigate(`/monitoring/${row.id}`);
      } else {
        navigate(`/history/alert/${row.id}`);
      }
    } else if (row.eventType === "journey") {
      navigate(`/history/journey/${row.id}`);
    } else if (row.eventType === "forbidden_zone") {
      navigate(`/history/forbidden-zone/${row.id}`);
    }
  }

  return (
    <List title={translate("history.titles", "History")}>
      <HistoryFilters filters={filters} onChange={handleFiltersChange} />
      <DataGrid
        rows={data}
        columns={columns}
        rowCount={total}
        loading={isLoading}
        paginationMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={[10, 25, 50, 100]}
        autoHeight
        filterModel={undefined}
        disableColumnFilter
        disableColumnMenu
        getRowClassName={(params) =>
          params.row.isActive ? "row-active" : ""
        }
        sx={{
          "& .row-active": {
            backgroundColor: "#e8f5e9",
            "&:hover": { backgroundColor: "#c8e6c9" },
          },
        }}
      />
    </List>
  );
};
